import React, { useEffect, useState } from "react";
import { PlSheet } from "../list/ProductListBits";
import { createShadeLink, shareableLink } from "./shadeApi";
import type { ShadeLink } from "./shadeApi";
import { SHADE_STRINGS as S } from "./shadeStrings";

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });

/**
 * Makes a fresh worker upload link when opened and shows it once: copy or share. A new link each time; old ones
 * keep working until they expire or the admin closes them in the Shade photos panel.
 */
const ShadeLinkSheet = ({ open, onClose, productId, onMade }: { open: boolean; onClose: () => void; productId: string; onMade?: () => void }) => {
  const [link, setLink] = useState<ShadeLink | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLink(null);
    setError("");
    setCopied(false);
    createShadeLink(productId)
      .then((res) => {
        if (!alive) return;
        if (res?.success && res.data) {
          setLink(res.data);
          onMade?.();
        } else setError(res?.message || S.error);
      })
      .catch(() => alive && setError(S.noConnection));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const url = link ? shareableLink(link) : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const canShare = typeof navigator !== "undefined" && typeof (navigator as any).share === "function";

  return (
    <PlSheet open={open} onClose={onClose} label={S.linkTitle} className="pu-sheet">
      {(close) => (
        <div className="pu-sheet__body">
          <h3 className="pu-sheet__title">{S.linkTitle}</h3>
          {!link && !error ? <p className="pu-dim">{S.making}</p> : null}
          {link ? (
            <>
              <p className="pu-dim">{S.linkBody(48, link.maxUploads)}</p>
              <input className="sp-linkbox" readOnly value={url} aria-label="Upload link" onFocus={(e) => e.target.select()} />
              <p className="pu-dim">{S.linkExpires(when(link.expiresAt))}</p>
              <div className="pu-actions">
                <button type="button" className="pl-btn is-primary" onClick={() => void copy()}>
                  {copied ? S.copied : S.copy}
                </button>
                {canShare ? (
                  <button type="button" className="pl-btn is-ghost" onClick={() => void (navigator as any).share({ title: S.linkTitle, url }).catch(() => undefined)}>
                    {S.share}
                  </button>
                ) : null}
                <button type="button" className="pl-btn is-ghost" onClick={close}>
                  {S.close}
                </button>
              </div>
            </>
          ) : null}
          {error ? (
            <div className="pu-error" role="alert">
              {error}
            </div>
          ) : null}
        </div>
      )}
    </PlSheet>
  );
};

export default ShadeLinkSheet;
